import React, { useState, useEffect } from 'react';
import { compatAPI } from '../../utils/api';
import useReleaseFilter from '../../hooks/useReleaseFilter';
import ReleaseFilterBar from '../../components/ReleaseFilterBar';

export default function CompatMatrixPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appVersions, setAppVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const filter = useReleaseFilter();

    useEffect(() => {
        compatAPI.getBuildsSeen(filter.days).then(r => {
            setAppVersions(r.data?.app_versions || []);
            setCurrentVersion(r.data?.current_app_version_name || null);
        }).catch(() => {});
    }, [filter.days]);

    useEffect(() => {
        setLoading(true);
        compatAPI.getMatrix(filter.queryParams)
            .then(res => setData(res.data))
            .catch(err => console.error('Matrix error:', err))
            .finally(() => setLoading(false));
    }, [filter.queryParams.release_op, filter.queryParams.release_value, filter.queryParams.days, filter.queryParams.layer]);

    // Backend at /admin/api/compat/matrix returns {data: [...rows]}.
    // Tolerate that shape plus the older {rows: [...]} and bare-array
    // shapes so the page survives backend tweaks.
    const rawRows = Array.isArray(data) ? data
                  : Array.isArray(data?.rows) ? data.rows
                  : Array.isArray(data?.data) ? data.data
                  : [];
    // Normalise the v1.11.20 compatibility_matrix view shape onto the
    // column names the table renders. (sink_name → device_name,
    // sink_mac_oui → oui, success_rate → verdict + error_rate.)
    const rows = rawRows.map(r => {
        const successRate = r.success_rate != null ? Number(r.success_rate) : null;
        return {
            device_name: r.device_name || r.sink_name || r.model || '-',
            oui:         r.oui || r.sink_mac_oui || '-',
            protocol:    r.protocol || '-',
            pipeline_path: r.pipeline_path,
            verdict:     r.verdict ||
                         (successRate != null && successRate >= 0.9 ? 'works' :
                          successRate != null && successRate >= 0.5 ? 'flaky' :
                          (r.failures || 0) > 0                     ? 'broken' :
                          'unknown'),
            session_count: r.session_count ?? r.total_attempts ?? 0,
            error_rate: r.error_rate != null
                ? Math.round(Number(r.error_rate) * 100) / 100
                : (successRate != null
                    ? Math.round((1 - successRate) * 1000) / 10
                    : 0),
        };
    });

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Compatibility Matrix</h2>
            <ReleaseFilterBar filter={filter} appVersions={appVersions} currentVersion={currentVersion} />
            {loading && <div className="text-gray-400 mb-2">Loading…</div>}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OUI</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Protocol</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verdict</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error %</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {rows.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.device_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{row.oui}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.protocol}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-mono">{row.pipeline_path || '-'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            row.verdict === 'works' ? 'bg-green-100 text-green-800' :
                                            row.verdict === 'flaky' ? 'bg-yellow-100 text-yellow-800' :
                                            row.verdict === 'broken' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {row.verdict}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.session_count}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.error_rate}%</td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan="7" className="px-6 py-6 text-sm text-gray-400 text-center">No compatibility data yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
